import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import clsx from 'clsx';

/** Единые классы контролов — чтобы input/select/textarea выглядели одинаково. */
export const CONTROL_CLASSES =
  'block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 ' +
  'placeholder:text-slate-400 disabled:cursor-not-allowed disabled:bg-slate-100 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200';

const ERROR_CLASSES = 'border-blocked focus:border-blocked focus:ring-red-200';

export interface FieldProps {
  label: ReactNode;
  /** Контрол или функция, получающая сгенерированный id для label/aria. */
  children: ReactNode | ((id: string) => ReactNode);
  required?: boolean;
  hint?: ReactNode;
  error?: string | null;
  className?: string;
}

export function Field({ label, children, required, hint, error, className }: FieldProps) {
  const id = useId();
  return (
    <div className={clsx('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-sm font-medium text-slate-700">
        {label}
        {required ? <span className="ml-0.5 text-blocked">*</span> : null}
      </label>
      {typeof children === 'function' ? children(id) : children}
      {error ? (
        <p className="text-sm text-blocked" role="alert">
          {error}
        </p>
      ) : null}
      {!error && hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={clsx(CONTROL_CLASSES, invalid && ERROR_CLASSES, className)}
      {...rest}
    />
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, className, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={clsx(CONTROL_CLASSES, 'pr-8', invalid && ERROR_CLASSES, className)}
      {...rest}
    >
      {children}
    </select>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, rows = 3, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={clsx(CONTROL_CLASSES, 'resize-y', invalid && ERROR_CLASSES, className)}
      {...rest}
    />
  );
});

/** Сетка полей: одна колонка на телефоне, две — от sm. */
export function FieldGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx('grid grid-cols-1 gap-4 sm:grid-cols-2', className)}>{children}</div>;
}
